import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { restaurantInfo, openingHours } from '../../data/mock';
import { handleImageError } from '../../lib/imageFallback';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1B4332] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/">
              <picture>
                <source type="image/webp" srcSet="/images/logo.webp" />
                <img 
                  src="/images/logo.png" 
                  alt="La Pasteria Pizza Bar" 
                  loading="lazy"
                  decoding="async"
                  onError={handleImageError}
                  className="h-16 w-auto mb-4 brightness-0 invert"
                />
              </picture>
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Authentic Italian cuisine in the heart of Dunstable. Fresh ingredients, 
              traditional recipes, unforgettable flavours.
            </p>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C67C4E] transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C67C4E] transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-semibold text-lg mb-6 flex items-center gap-2">
              <Clock size={20} className="text-[#C67C4E]" />
              Opening Hours
            </h4>
            <ul className="space-y-2">
              {openingHours.map((item) => (
                <li key={item.day} className="flex justify-between text-gray-300">
                  <span>{item.day}</span>
                  <span>{item.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  <Phone size={18} className="text-[#C67C4E]" />
                  {restaurantInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${restaurantInfo.email}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  <Mail size={18} className="text-[#C67C4E]" />
                  {restaurantInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <MapPin size={18} className="text-[#C67C4E] mt-1 flex-shrink-0" />
                <span>{restaurantInfo.address}</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/order"
                  className="text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  Menu / Order
                </Link>
              </li>
              <li>
                <Link
                  to="/booking"
                  className="text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  Book a Table
                </Link>
              </li>
              <li>
                <a
                  href={restaurantInfo.tripAdvisorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  TripAdvisor
                </a>
              </li>
              <li>
                <a
                  href={restaurantInfo.googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-[#C67C4E] transition-colors"
                >
                  Google Reviews
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-400 space-y-3">
          <div>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-[#C67C4E] hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
          <p>
            &copy; {currentYear} La Pasteria Pizza Bar. All rights reserved. | Website Made by{' '}
            <a 
              href="https://hinton-media.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#C67C4E] hover:text-white transition-colors"
            >
              Hinton Media
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
